// src/projectsApi.ts
// Thin client for /api/projects/* and /api/jobs/* — see api/src/projects.ts
// and api/src/jobs.ts. Rendered outputs come from each job's same-origin
// /output URL: captions with a plain link, videos in ranged chunks.

import { apiFetch, ApiError } from './api';
import { fetchInChunks, RangedFetchError } from './rangedFetch';
import type {
  Project, ProjectCreate, ProjectSummary, ProjectUpdate, JobSummary, WorkerStatusSummary,
} from '@ring/shared/projects';

export type { Project, ProjectSummary, JobSummary, WorkerStatusSummary };

export async function listProjects(): Promise<ProjectSummary[]> {
  return (await apiFetch<{ projects: ProjectSummary[] }>('/api/projects', { method: 'GET' })).projects;
}

export function createProject(body: ProjectCreate): Promise<Project> {
  return apiFetch<Project>('/api/projects', { method: 'POST', body: JSON.stringify(body) });
}

export function getProject(projectId: string): Promise<Project> {
  return apiFetch<Project>(`/api/projects/${projectId}`, { method: 'GET' });
}

export function updateProject(projectId: string, update: ProjectUpdate): Promise<Project> {
  return apiFetch<Project>(`/api/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(update) });
}

export function deleteProject(projectId: string): Promise<void> {
  return apiFetch<void>(`/api/projects/${projectId}`, { method: 'DELETE' });
}

export interface JobList {
  jobs:   JobSummary[];
  worker: WorkerStatusSummary;
}

export function listJobs(opts: { projectId?: string; limit?: number } = {}): Promise<JobList> {
  const params = new URLSearchParams();
  if (opts.projectId) params.set('projectId', opts.projectId);
  if (opts.limit)     params.set('limit', String(opts.limit));
  // toString(), not params.size: size is missing in Safari before 17, which would silently drop the filter.
  const query = params.toString() ? `?${params}` : '';
  return apiFetch<JobList>(`/api/jobs${query}`, { method: 'GET' });
}

export function queueRender(projectId: string): Promise<JobSummary> {
  return apiFetch<JobSummary>('/api/jobs', { method: 'POST', body: JSON.stringify({ type: 'render', projectId }) });
}

/** Cancels a render that hasn't started yet (409 once the worker has it). */
export function cancelJob(jobId: string): Promise<JobSummary> {
  return apiFetch<JobSummary>(`/api/jobs/${jobId}/cancel`, { method: 'POST' });
}

export function jobOutputUrl(jobId: string, format: 'mp4' | 'srt' = 'mp4'): string {
  return `/api/jobs/${jobId}/output${format === 'srt' ? '?format=srt' : ''}`;
}

/**
 * Downloads a large output in ranged chunks and saves it, instead of one
 * plain GET: the server won't buffer a whole video in one response (see
 * jobs.ts's MAX_WHOLE_OUTPUT_BYTES). Holds the file in memory until it's
 * saved, which is fine for a video ad (at most a few hundred MB).
 */
export async function downloadInChunks(url: string, onProgress?: (fraction: number) => void): Promise<void> {
  let file;
  try {
    file = await fetchInChunks(url, onProgress);
  } catch (err) {
    throw err instanceof RangedFetchError ? new ApiError(err.status, err.message) : err;
  }
  const filename = /filename="([^"]+)"/.exec(file.headers.get('Content-Disposition') ?? '')?.[1] ?? 'video.mp4';
  const objectUrl = URL.createObjectURL(file.blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the save before the bytes are released.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
